/**
 * Capital doors — where money actually entered this system, and what it cost.
 *
 * WHY THIS EXISTS
 * `netGrowthUSD` measured "capital" with a note-text DENYLIST: eight lowercase prefixes,
 * and any cash row that matched none of them was external capital. A denylist over free
 * text has exactly one failure mode and it is the dangerous one — anything unforeseen is
 * silently counted. The 2026-08-02 audit priced that failure on live data:
 *
 *   +$17,273  one `Open TRYVD2` row typed `deposit` instead of `td_open`. A time deposit
 *             being opened — the user's own money moving one bucket left — read as capital
 *             arriving from outside.
 *   +$10,515  a ₺500,000 deposit with an EMPTY note. No rule matched, so it defaulted to
 *             capital. Nothing anywhere said what it was.
 *   -$12,974  `XAU trade open cost (correction)`, an internal correction, subtracted from
 *             capital as though money had left the household.
 *
 * and, in the other direction, ₺616,912 was EXCLUDED purely because the note happened to
 * contain the substring "cash added". Free text was deciding the balance sheet.
 *
 * THE RULE HERE IS THE OPPOSITE ONE. Classification is structural (`tx.type`, `_audit`,
 * `_divId`, `tradeId`) with a small explicit note table underneath it, and anything that
 * matches nothing is UNKNOWN — never external, never internal, never quietly zero. Unknown
 * rows are returned with their dates and amounts so they can be shown to the person who
 * knows what they were, which is not this module.
 *
 * FOUR DOORS, NOT ONE. The cash ledger is one way capital enters. Live state had three more
 * that no consumer had ever counted:
 *   - `rfTxs`          the reserve fund, including 15.09g XAU and 111.27g XAG
 *   - plan lots        SAP payroll-plan `investment` lots, which never touch the cash ledger
 *   - opening balances holdings entered with a cost and no lot, no month, no transaction
 *
 * NO IMPORTS, NO STATE WRITES, NO NETWORK — same contract as integrity.js. Every conversion
 * is injected so this file cannot disagree with the rest of the app about an FX rate, and
 * so it is testable without the state singleton.
 */

export const TX_CLASS = {
  EXTERNAL_IN: 'external_in',    // real money crossing INTO the system
  EXTERNAL_OUT: 'external_out',  // real money crossing OUT (spent, repaid)
  INTERNAL: 'internal',          // the user's own money moving between his own buckets
  MIRROR: 'mirror',              // accounting mirror, no money moved at all
  UNKNOWN: 'unknown',            // no rule matched — MUST NOT be counted as anything
};

/**
 * Transaction types that describe a bucket-to-bucket move by construction.
 * These need no note parsing: opening a position, adding to one, closing one, opening or
 * topping up a deposit are all internal by definition of what the type MEANS.
 */
const INTERNAL_TYPES = new Set([
  'portfolio_buy', 'trade_open', 'trade_add', 'trade_exit',
  'td_open', 'td_close', 'td_topup',
  // Both legs of a currency conversion. The owner's own money changing denomination is
  // never capital entering or leaving, and now the ledger says so structurally instead of
  // leaving two unrelated rows in two currencies. (audit 2026-08-02)
  'fx_convert',
  // ACCT-002, raised 2026-08-10 and closed 2026-08-14. These three are written by the app and
  // were not readable by it: `classifyCashTx` falls straight to
  // `UNKNOWN — unrecognised type` for anything that is neither listed here nor a
  // `deposit`/`withdraw`, and ONE unclassified row flips `reconciled` to false, which zeroes
  // `netExtCapital` for that day's snapshot.
  //
  // All three are the owner's own money changing pocket, which is the same reasoning
  // `fx_convert` carries:
  //   `rf_deposit` / `rf_withdraw` — cash ⇄ reserve fund. `rfAction` writes them ONLY on the
  //       `fromCash` branch, so by construction they never cross the system boundary.
  //   `td_mature`  — a time deposit paying back principal + net interest into cash
  //       (`portfolio.js:667`). Its sibling `td_close` was already here; the maturity path was
  //       missed, which is the same one-of-N-call-sites shape as everything else this week.
  //
  // Pinned by a SOURCE SWEEP, not by this list being remembered: `internal-types.test.js`
  // walks `src/` for every type written into `S.cashTxs` and fails on one this cannot read.
  'rf_deposit', 'rf_withdraw', 'td_mature',
]);

/**
 * `tx.reason` is a CONTROLLED VOCABULARY — the Cash modal offers a fixed list and only
 * "Other" is free text. That makes it a far better classifier than the note, which is prose
 * the user writes for himself. Consulted before the note rules for exactly that reason.
 *
 * A reason not listed here falls through to the notes and then to UNKNOWN. Adding a new
 * option to the Cash dropdown without adding it here makes rows unclassified, which is
 * loud — the alternative, defaulting them to capital, is what this module exists to stop.
 */
const REASON_CLASS = {
  'Salary': TX_CLASS.EXTERNAL_IN,
  'Bonus': TX_CLASS.EXTERNAL_IN,
  'Freelance': TX_CLASS.EXTERNAL_IN,
  'Refund': TX_CLASS.EXTERNAL_IN,
  'Gift': TX_CLASS.EXTERNAL_IN,
  'Transfer In': TX_CLASS.EXTERNAL_IN,
  'Transfer Out': TX_CLASS.EXTERNAL_OUT,
  'Investment': TX_CLASS.EXTERNAL_OUT,
  'Tax Payment': TX_CLASS.EXTERNAL_OUT,
  'Major Purchase': TX_CLASS.EXTERNAL_OUT,
  // A bill or instalment paid from savings rather than from the month's salary. Added 2026-10-13:
  // the owner had to record September's school instalment and every outbound reason named a
  // purchase, a transfer or a tax. Classified EXTERNAL_OUT like the rest: money spent, not moved.
  'Living Expense': TX_CLASS.EXTERNAL_OUT,
};

/**
 * The reason vocabulary, BY DIRECTION, derived from the classifier that gives it meaning.
 *
 * `cash.js` carried its own hand-written `_DEPOSIT_REASONS` / `_WITHDRAW_REASONS`, and
 * `index.html` a third copy as static `<option>` elements. Three lists that had to agree and
 * nothing made them — the shape that has cost this project a dead Import button, a restore that
 * dropped fifteen keys and a deploy gate that ran 49 of 72 files. Adding a reason to
 * REASON_CLASS above now offers it everywhere. ⚠ THE HTML NO LONGER HOLDS A LIST AT ALL (2026-08-16):
 * `cash._populateReasons` paints the two optgroups from this map, so the third copy is gone rather
 * than pinned. `one-reason-vocabulary.test.js` is the guard — the earlier note here named a
 * `reason-vocabulary.test.js` that never existed, while the real assertion lived in
 * `tests/modules/ai-actions-money.test.js`. Name the file that exists.
 *
 * A row whose reason is not in here is not refused — "Other" is deliberately free text, and an
 * unrecognised reason falls to the note rules and then to UNKNOWN, which is loud. What these
 * lists are for is the paths that have no dropdown: ai-actions.js writes cash from chat, and a
 * reason it invented would be inference presented as fact. (2026-08-14)
 */
/**
 * WHERE THE DOLLARS WENT — a controlled vocabulary, because two readers were guessing.
 *
 * `txClass` answers whether money crossed the system boundary. Nothing answered where a
 * conversion's proceeds went, so `cashflow.js` and `integrity.js` both read the currency pair
 * instead and concluded that EVERY TRY->USD conversion was the monthly IBKR plan wire.
 *
 * That held only because it had never been tested: all six fx_convert rows in the live book on
 * 2026-09-11 were IBKR wires. Converting lira for anything else -- a car, or the Garanti BBVA
 * Kripto account, which is dollar-denominated and shows its own USD fiat balance -- would have
 * been reported as plan funding, and the month's reconciliation would be wrong by the amount.
 *
 * Same reasoning as REASON_CLASS one screen up: a controlled value the writer sets beats prose
 * a reader has to interpret, and it beats a structural coincidence even harder.
 */
export const FX_PURPOSE = Object.freeze({
  IBKR: 'ibkr',      // the monthly plan wire -- the ONLY purpose that is plan funding
  CRYPTO: 'crypto',  // Garanti BBVA Kripto: lira in, dollars out, dollars buy coins
  OTHER: 'other',    // any other reason the owner changes denomination
});

/** Human labels for the modal. Kept beside the values so the two cannot drift. */
export const FX_PURPOSE_LABEL = Object.freeze({
  ibkr: 'IBKR funding (monthly plan wire)',
  crypto: 'Garanti BBVA Kripto',
  other: 'Other',
});

/**
 * A row's purpose, or the default for one written before the field existed.
 *
 * The default is `ibkr` deliberately: every conversion in the book when this shipped was an
 * IBKR wire (measured, six rows), so an unstamped row reads exactly as it did before -- the
 * degradation is back to the old behaviour, not into silence. v58 stamps those six, and
 * integrity check `cash.fxPurpose` names anything still unstamped, so the fallback cannot do
 * quiet work.
 */
export function fxPurposeOf(tx) {
  const v = tx && tx.fxPurpose;
  return Object.values(FX_PURPOSE).indexOf(v) !== -1 ? v : FX_PURPOSE.IBKR;
}

/** Is this conversion leg part of the monthly plan funding? The one question both readers ask. */
export function isPlanFunding(tx) {
  return fxPurposeOf(tx) === FX_PURPOSE.IBKR;
}

export const DEPOSIT_REASONS = Object.freeze(
  Object.keys(REASON_CLASS).filter(k => REASON_CLASS[k] === TX_CLASS.EXTERNAL_IN));
export const WITHDRAW_REASONS = Object.freeze(
  Object.keys(REASON_CLASS).filter(k => REASON_CLASS[k] === TX_CLASS.EXTERNAL_OUT));

/**
 * What the Cash dropdown SHOWS for a reason, where the stored value alone is not self-explaining.
 *
 * ⚠ THE VALUE AND THE LABEL BELONG TOGETHER, AND THEY WERE NOT. `index.html` carried the ten
 * built-in `<option>` elements by hand — the THIRD copy of this vocabulary — and four of them
 * said more than the value does: *"Freelance / Side Income"*, *"Transfer In (from other
 * account)"*. Deriving the list without them would have deleted a hint the owner reads, so the
 * hint moved here instead of the list staying there. A reason with no entry shows its own value.
 *
 * ⚠ A LABEL FOR A REASON NOBODY CAN PICK IS DEAD, so `one-reason-vocabulary.test.js` fails on a
 * key here that `REASON_CLASS` does not have. (2026-08-16)
 */
const REASON_LABEL = {
  Freelance: 'Freelance / Side Income',
  'Transfer In': 'Transfer In (from other account)',
  'Transfer Out': 'Transfer Out (to other account)',
  Investment: 'Investment (external)',
  'Living Expense': 'Living Expense (bill / instalment from savings)',
};
export const REASON_LABELS = Object.freeze({ ...REASON_LABEL });
/** The display text for a reason value. Identity when it needs no explaining. */
export function reasonLabel(value) {
  return REASON_LABEL[value] || String(value == null ? '' : value);
}

/**
 * Note rules for `deposit` / `withdraw`, the only two ambiguous types.
 * ORDER MATTERS — first match wins. Every entry states a class explicitly; there is no
 * "everything else is external" arm, by design.
 */
const NOTE_RULES = [
  { re: /^salary:/i,            cls: TX_CLASS.MIRROR,       rule: 'salary mirror',       door: 'Cash Flow → the month\'s income entry' },
  { re: /^dividend:/i,          cls: TX_CLASS.INTERNAL,     rule: 'dividend credit (paired with reinvest)', door: 'Dividends' },
  { re: /^drip reinvest:/i,     cls: TX_CLASS.INTERNAL,     rule: 'DRIP reinvest (paired with credit)',     door: 'Dividends' },
  { re: /^undo expense:/i,      cls: TX_CLASS.INTERNAL,     rule: 'expense reversal',    door: 'Cash Flow → the month' },
  { re: /^expense:/i,           cls: TX_CLASS.EXTERNAL_OUT, rule: 'expense leaves the system', door: 'Cash Flow → the month' },
  { re: /^liability payment:/i, cls: TX_CLASS.EXTERNAL_OUT, rule: 'debt repayment leaves the system', door: 'Liabilities → the payment row' },
  { re: /^monthly ibkr deposit/i, cls: TX_CLASS.EXTERNAL_IN, rule: 'monthly brokerage transfer', door: 'Monthly → the reconciled month' },
  { re: /^external deposit/i,   cls: TX_CLASS.EXTERNAL_IN,  rule: 'declared external deposit' },
  { re: /^transfer in\b/i,      cls: TX_CLASS.EXTERNAL_IN,  rule: 'transfer in' },
  { re: /^one-?time/i,          cls: TX_CLASS.EXTERNAL_IN,  rule: 'one-time income (bonus)', door: 'Money → One-time income' },
  { re: /^income:/i,            cls: TX_CLASS.EXTERNAL_IN,  rule: 'recorded income',     door: 'Cash Flow → the month\'s income entry' },
  // Deliberately NOT rules, and this is the point of the module:
  //
  //   "cash added"   — used to EXCLUDE ₺616,912 on a substring match.
  //   "Open <X>"     — a first draft of this file mapped it to INTERNAL, to catch the
  //                    ₺821,350 `Open TRYVD2` row typed as a deposit. But that is the same
  //                    sin pointing the other way: reading intent out of prose. The row has
  //                    a paired `trade_open` of the identical amount, which makes it look
  //                    internal, and it is ALSO the only thing that could have funded a
  //                    deposit the app had no cash for — which makes it look external.
  //                    The owner settled it: he added the money in order to open the
  //                    deposit, so it is capital. No amount of reading the note would have
  //                    established that, which is exactly why the note must not decide it.
  //   "(correction)" — same reasoning, same pair.
  //
  // The test for a rule belonging here is not "can I usually guess right". It is "is this
  // fact, or is it inference". Inference goes to the owner.
];

/**
 * WHO WROTE THIS ROW — and therefore which door may delete it.
 *
 * The Cash page offers a Del button, and until 2026-08-10 it offered one on every row. That was
 * narrowed to `deposit` and `withdraw` and given an `_audit` guard, which sounded like the fix
 * and was not: **44 of the 47 deletable rows on the live book are still system-written.**
 * Measured 2026-08-14 —
 *
 *   22  dividend credit / DRIP reinvest   mirrors of a `dividends` record
 *    7  monthly brokerage transfer        reconcileMo's deposit half, whose `portfolio_buy`
 *                                         twin is NOT deletable, so deleting it breaks the pair
 *    5  _audit salary mirrors             already guarded, and self-healing
 *    2  one-time income                   TRY 698,711 — deleting orphans the OTI record
 *    2  liability payment                 TRY 1,461,280 — paired with a payment record
 *    3  genuinely manual                  Transfer In, declared external deposit
 *
 * Every one of the 44 has a twin somewhere else in the state. Reversing the cash and leaving the
 * twin standing is the same one-legged write this codebase has now found four times, pointing
 * backwards: the DELETE is the inverse, and an inverse that undoes one of two legs destroys or
 * creates money exactly as reliably as a forward path that only writes one.
 *
 * TYPE IS NOT PROVENANCE. `deposit` says what the row does to a balance; it says nothing about
 * who wrote it. The link fields and the note prefixes above do — and the prefixes are already
 * enumerated for the classifier, so this reads them rather than restating them.
 *
 * @returns {{system: boolean, why: string, door: string}} — `door` names where it CAN be undone.
 */
export function cashTxOrigin(tx) {
  if (!tx) return { system: false, why: '', door: '' };
  if (tx._audit === true) return { system: true, why: 'an audit mirror of recorded income', door: 'Cash Flow → the month\'s income entry' };
  const links = [
    ['_otiId', 'a one-time income record', 'Money → One-time income'],
    ['_divId', 'a dividend record', 'Dividends'],
    ['_incomeId', 'a monthly income entry', 'Cash Flow → the month\'s income entry'],
    ['tradeId', 'a trade', 'Trades'],
    ['fxId', 'the other leg of a currency conversion', 'Cash → the conversion'],
    // A link is a fact, a note is prose. v51 back-links the legacy rows so this beats the
    // `^liability payment:` NOTE_RULES entry, and names the row rather than the page — until
    // 2026-08-14 that page had no un-pay at all and did not even render the payments.
    ['_paymentId', 'a liability payment', 'Liabilities → the payment row'],
  ];
  for (const [k, why, door] of links) {
    if (tx[k]) return { system: true, why, door };
  }
  const note = String(tx.note || '');
  for (const r of NOTE_RULES) {
    if (r.door && r.re.test(note)) return { system: true, why: r.rule, door: r.door };
  }
  return { system: false, why: '', door: '' };
}

/**
 * Classify one cash-ledger row.
 * @returns {{cls: string, rule: string}} — cls is always one of TX_CLASS.
 */
export function classifyCashTx(tx) {
  if (!tx) return { cls: TX_CLASS.UNKNOWN, rule: 'null row' };

  // 1. An explicit class written at creation time always wins. New writers should set it;
  //    everything below is the archaeology needed for rows written before it existed.
  if (tx.txClass && Object.values(TX_CLASS).indexOf(tx.txClass) !== -1) {
    return { cls: tx.txClass, rule: 'explicit txClass' };
  }

  // 2. Structural facts, in descending order of how hard they are to fake.
  if (tx._audit === true) return { cls: TX_CLASS.MIRROR, rule: '_audit mirror row' };
  if (INTERNAL_TYPES.has(tx.type)) return { cls: TX_CLASS.INTERNAL, rule: 'type ' + tx.type };
  if (tx._divId) return { cls: TX_CLASS.INTERNAL, rule: 'linked to a dividend record' };
  if (tx.tradeId) return { cls: TX_CLASS.INTERNAL, rule: 'linked to a trade' };
  if (tx._otiId) return { cls: TX_CLASS.EXTERNAL_IN, rule: 'linked to one-time income' };

  if (tx.type !== 'deposit' && tx.type !== 'withdraw') {
    return { cls: TX_CLASS.UNKNOWN, rule: 'unrecognised type ' + (tx.type || '(none)') };
  }

  // 3. The controlled reason vocabulary, before any prose.
  if (tx.reason && REASON_CLASS[tx.reason]) {
    return { cls: REASON_CLASS[tx.reason], rule: 'reason ' + tx.reason };
  }

  // 4. Note rules — last resort, explicit, ordered.
  const note = String(tx.note || '').trim();
  if (!note) return { cls: TX_CLASS.UNKNOWN, rule: 'empty note on a cash move' };
  for (const r of NOTE_RULES) {
    if (r.re.test(note)) return { cls: r.cls, rule: r.rule };
  }
  return { cls: TX_CLASS.UNKNOWN, rule: 'no rule matched' };
}

/** Sum helper that treats a non-finite conversion as a hole to report, not a zero. */
function _add(acc, usd) {
  if (typeof usd !== 'number' || !isFinite(usd)) { acc.bad++; return; }
  acc.sum += usd;
}

/**
 * Every door capital can enter through, plus the internal sources that add wealth without
 * any capital behind them.
 *
 * @param {Object} S    state (not mutated)
 * @param {Object} ctx  { toUSDAt(v, cur, dateISO), toUSD(v, cur), costOfHolding(h) }
 *                      costOfHolding must be the SAME cost basis the rest of the app uses
 *                      (math.hTotUSD) so this module can never invent a sixth definition.
 */
export function capitalDoors(S, ctx, opts) {
  const c = ctx || {};
  const now = typeof c.toUSD === 'function' ? c.toUSD : ((v) => v);
  // TWO BASES, ON PURPOSE.
  //
  // Event-time answers "how much did I actually put in" — the right question for a capital
  // figure, and the one the doors report. But the identity compares those doors against
  // assets valued at TODAY's rates, and TRY moved from 42.94 to 47.55 across this history.
  // Mixing the two instants put $2,183 of pure currency drift into the residual, which then
  // reads as a missing row. It is not a missing row; it is the same money seen from two
  // dates.
  //
  // So the identity runs on spot for BOTH sides, and the difference between the two runs is
  // reported as fxDriftUSD rather than swept into a tolerance. (audit 2026-08-02)
  const spotBasis = !!(opts && opts.basis === 'spot');
  const at = spotBasis ? ((v, cur) => now(v, cur))
    : (typeof c.toUSDAt === 'function' ? c.toUSDAt : ((v) => v));
  const costOf = typeof c.costOfHolding === 'function' ? c.costOfHolding : (() => 0);

  // ---- Door 1: the cash ledger -------------------------------------------------------
  const inAcc = { sum: 0, bad: 0 }, outAcc = { sum: 0, bad: 0 };
  const unknown = [];
  (S && S.cashTxs || []).forEach(tx => {
    if (!tx) return;
    const { cls, rule } = classifyCashTx(tx);
    const usd = at(Math.abs(tx.amount || 0), tx.currency || 'TRY', tx.date);
    if (cls === TX_CLASS.EXTERNAL_IN) _add(inAcc, usd);
    else if (cls === TX_CLASS.EXTERNAL_OUT) _add(outAcc, usd);
    else if (cls === TX_CLASS.UNKNOWN) {
      unknown.push({ id: tx.id, date: tx.date, type: tx.type, amount: tx.amount,
        currency: tx.currency, note: tx.note || '', usd: isFinite(usd) ? usd : null, rule });
    }
  });

  // ---- Door 2: the reserve fund ------------------------------------------------------
  // Its own ledger, its own currencies, metals included. Nothing in the app had ever
  // counted it as capital, so the gold and silver sat in net worth with no cost behind it.
  //
  // ⚠ ACCT-001 — A RESERVE DEPOSIT PAID FOR OUT OF TRACKED CASH IS NOT NEW OUTSIDE MONEY.
  //
  // This door counted EVERY reserve row as external. `INTERNAL_TYPES` correctly declines to
  // count the `rf_deposit` cashTx that funded it — cash → reserve is the owner's own money
  // changing pocket — so the money is counted exactly once on the cash side and then a SECOND
  // time here. One event, two claims of new capital.
  //
  // It could not be fixed before 2026-08-14 because the reserve ledger did not record where a
  // deposit came from. **v52 (`43aa8c1`) added `funded`, and MOVE-002's whole point was that an
  // inverse cannot know what to undo unless the forward path wrote it down — the same field
  // turns out to be what this door needed to stop double-counting.**
  //
  // Measured on the live book 2026-08-15: five reserve rows. Four are the 2026-05-02 Akbank
  // deposits (USD 150, EUR 150, XAG 111.27 g, XAU 15.09 g) — genuinely outside money, still
  // counted. One is the ₺34,000 of 2026-05-24, `funded: 'cash'`, linked to
  // `0msu0cs3700j7aa2y`. Its money entered the system ONCE, through
  // `mpk4h8zlqn01a` — `reason: 'Transfer In'`, which `REASON_CLASS` classes EXTERNAL_IN — and
  // was then moved into the reserve. **≈ $710 of contributed capital that never arrived.**
  //
  // `funded: 'external'` and unrecorded rows both stay external. Unrecorded is the honest
  // default for legacy: it preserves today's answer for the four Akbank rows, and every row
  // written since v52 carries the field. It is NOT silent — the excluded total is reported as a
  // memo door below, because a door that quietly declines to count something is
  // indistinguishable from one that forgot to.
  const rfAcc = { sum: 0, bad: 0 };
  const rfFromCash = { sum: 0, bad: 0 };
  (S && S.rfTxs || []).forEach(tx => {
    if (!tx) return;
    const usd = at(Math.abs(tx.amount || 0), tx.currency || 'TRY', tx.date);
    const signed = (tx.type === 'withdraw') ? -usd : usd;
    _add(tx.funded === 'cash' ? rfFromCash : rfAcc, signed);
  });

  // ---- Doors 3 and 4: holdings whose capital never passed through a ledger -----------
  // A holding is an employer plan when it carries BOTH kinds of lot: `investment` (the
  // employee's payroll money, external) and `drip` (the employer's match, free). Splitting
  // them is the whole point — one is capital, the other is income, and they arrive in the
  // same transaction.
  const planAcc = { sum: 0, bad: 0 }, matchAcc = { sum: 0, bad: 0 }, openAcc = { sum: 0, bad: 0 };
  const openingBalances = [];
  const monthTickers = new Set();
  Object.values((S && S.monthlyRates) || {}).forEach(r => {
    Object.keys((r && r.entries) || {}).forEach(t => monthTickers.add(t));
  });

  (S && S.holdings || []).forEach(h => {
    // Same rule as assetsAtCostUSD: a position that is gone is neither an opening balance nor
    // a plan contribution. Both sides of the identity have to agree about what a holding is.
    if (!h || h.type === 'Cash' || !(h.qty > 0)) return;
    const lots = h.lots || [];
    if (lots.length) {
      // A lot PAID FOR OUT OF THE TRACKED CASH LEDGER is not capital entering the system.
      // That money already came in through cash_ledger_in and left again through a
      // portfolio_buy, which INTERNAL_TYPES correctly declines to count. Counting the lot as
      // well books the same dollar twice.
      //
      // This door was written when the only lot-bearing rows were the SAP employer plan and
      // TEJ, so "has lots" and "arrived from outside" were the same set. The moment saveAddLot
      // began writing a funding leg they stopped being: seven ETFs bought with sale proceeds
      // on 2026-08-06 added USD 6,338.17 of phantom external capital and turned +3.78% net
      // growth into -4.62%. `funded` is stamped by the writer that also books the cash, so an
      // unstamped lot still means what it always meant. (audit 2026-08-06)
      const inv = lots.filter(l => l && l.type !== 'drip' && l.funded !== 'cash');
      const drp = lots.filter(l => l && l.type === 'drip');
      const cur = h.currency || 'USD';
      inv.forEach(l => _add(planAcc, at(l.cost || (l.qty || 0) * (l.price || 0), cur, l.date)));
      drp.forEach(l => _add(matchAcc, at(l.cost || (l.qty || 0) * (l.price || 0), cur, l.date)));

      // ⚠ ONE LOT USED TO CLOSE THE OPENING-BALANCE DOOR, AND THAT IS WHY a5 KEPT COMING BACK.
      //
      // This branch ended in `return`. `opening_balances` below is the door that accounts for
      // cost basis with no ledger trace, so a single lot made it unreachable and **recording one
      // purchase against a seeded position deleted that position's whole basis from contributed
      // capital.** The two sides then disagreed about the same holding: assetsAtCostUSD counts
      // `costOf(h)` -- all of `qty * avgCost` -- while the doors credited only the lots.
      //
      // The rule was already written, one function down, on the `qty > 0` guard: "Both sides of
      // the identity have to agree about what a holding is." And `hTotUSD` says it from the asset
      // side -- "lots become additive provenance rather than an all-or-nothing basis" (the ASELS
      // case, TRY 41,736 of averaged basis with no lots). The doors never learned it.
      //
      // Measured 2026-10-02 on the live book, outside the monthly plan:
      //
      //     BTC  0.05974  @ 92,670.71   $5,536
      //     ETH  1.56882  @  3,117.60   $4,891
      //     XRP  237.6517 @      1.937  $  460
      //                                --------
      //                                 $10,887
      //
      // All three carry ZERO cash rows -- the ledger references none of them -- so before a lot
      // existed `openAcc` counted them. The lots entered on 2026-09-11 and 2026-09-22 closed the
      // door on all three and took the residual from -$2,402 (116% of band) to +$8,596 (417%),
      // flipping its sign and hiding the older breach underneath.
      //
      // `costOf(h)` minus the lots is the part no lot describes. It has no date -- an opening
      // balance never does -- so it converts at spot on both bases, exactly as the branch below
      // has always done.
      //
      // A monthly-plan row is still excluded: its funding is explained by the monthly deposit
      // door, and crediting it here is the double-count that guard exists to prevent.
      if (monthTickers.has(h.ticker)) return;
      const lotBasis = lots.reduce((sum, l) =>
        (l && l.type !== 'drip') ? sum + (l.cost || (l.qty || 0) * (l.price || 0)) : sum, 0);
      const beyond = costOf(h) - now(lotBasis, cur);
      // A NEGATIVE `beyond` is a DIFFERENT defect -- lots claiming more basis than the position
      // holds, the shape a partial exit leaves -- and it is counted rather than clamped, so it
      // cannot hide. `capital.lotBasisGap` reports it either way.
      if (typeof beyond === 'number' && isFinite(beyond) && Math.abs(beyond) > 0.005) {
        _add(openAcc, beyond);
        openingBalances.push({ ticker: h.ticker, type: h.type, usd: beyond, beyondLots: true });
      }
      return;
    }
    // No lots and no monthly purchase record: the cost basis exists but nothing says where
    // the money came from. Opening balances — real capital, invisible to every ledger.
    if (monthTickers.has(h.ticker)) return;
    const usd = costOf(h);
    if (typeof usd === 'number' && isFinite(usd) && Math.abs(usd) > 0.005) {
      _add(openAcc, usd);
      openingBalances.push({ ticker: h.ticker, type: h.type, usd });
    }
  });

  // ---- Internal sources: wealth with no capital behind it -----------------------------
  const realAcc = { sum: 0, bad: 0 };
  (S && S.realized || []).forEach(r => {
    if (!r) return;
    // `pnlUSD` is stamped at close time, so on the spot basis it has to be reconverted or
    // it smuggles an event-time figure into a spot-time identity.
    const usd = (!spotBasis && typeof r.pnlUSD === 'number')
      ? r.pnlUSD : at(r.pnl || 0, r.currency || 'TRY', r.date);
    _add(realAcc, usd);
  });

  // Match dividends that produced no lot at all are income the portfolio never turned into
  // shares. Those that DID produce a lot are already counted in matchAcc.
  //
  // `lotId` is NOT a sufficient test: the eleven seeded SAP rows carry no lotId even though
  // their drip lots exist, so keying on it counted the same match twice and inflated the
  // figure to $10,206 against a true ~$5,100. Key on whether the TICKER has drip lots at
  // all — if it does, matchAcc already has this money. (live check 2026-08-02)
  const dripTickers = new Set();
  (S && S.holdings || []).forEach(h => {
    if (h && (h.lots || []).some(l => l && l.type === 'drip')) dripTickers.add(h.ticker);
  });
  const orphanMatch = { sum: 0, bad: 0 };
  (S && S.dividends || []).forEach(d => {
    if (!d || d.kind !== 'match') return;
    if (d.lotId || dripTickers.has(d.ticker)) return;
    _add(orphanMatch, now(d.net || d.gross || 0, d.currency || 'USD'));
  });

  // Dividends actually paid into cash (not reinvested, not employer match) raise the cash
  // balance with no capital behind them, so they belong in the identity.
  const cashDiv = { sum: 0, bad: 0 };
  (S && S.dividends || []).forEach(d => {
    if (!d || d.kind === 'match' || d.drip) return;
    _add(cashDiv, (!spotBasis && typeof d.netUSD === 'number' && isFinite(d.netUSD))
      ? d.netUSD : now(d.net || d.gross || 0, d.currency || 'USD'));
  });

  // ---- Liabilities are NOT a door ----------------------------------------------------
  // Tried and rejected: a `borrowed_principal` door summing liabilities.originalAmount.
  //
  // It rested on a wrong assumption, corrected by the owner: `S.liabilities` here are debts
  // he OWED, not loans he DREW. Nothing ever arrived because of them. Both live entries —
  // "USD - Home Bulk Debt" and "XAU - Home Bulk Debt" — were settled by liquidating his own
  // assets: he broke a gold position and a time deposit and paid them off, then marked both
  // as paid, which is why the liabilities balance is zero.
  //
  // So a door valuing them as incoming capital was not merely double-counting, it was
  // counting an inflow that never happened — $30,497 of it. What the ledger actually holds
  // is the truth of the matter: the assets arriving (₺616,912 of gold brought in, ₺821,350
  // added to open the deposit) and the money leaving (`liability payment:` rows). That is
  // all there is, and it is already counted.
  //
  // A debt denominated in a metal made it worse: pricing the principal at today's spot
  // against a repayment converted at event-time TRY mixes two instants and manufactures a
  // residual that is really just a gold move.

  const doors = [
    { key: 'cash_ledger_in', label: 'Cash ledger — money in', usd: inAcc.sum, kind: 'external' },
    // (byCurrency, assembled below, splits every one of these by denomination)
    { key: 'cash_ledger_out', label: 'Cash ledger — money out', usd: -outAcc.sum, kind: 'external' },
    { key: 'reserve_fund', label: 'Reserve fund ledger (incl. metals)', usd: rfAcc.sum, kind: 'external' },
    { key: 'plan_lots', label: 'Employer-plan contributions (investment lots)', usd: planAcc.sum, kind: 'external' },
    { key: 'opening_balances', label: 'Opening balances (no lot, no month, no tx)', usd: openAcc.sum, kind: 'external' },
    { key: 'realized_pnl', label: 'Realized P&L retained in the system', usd: realAcc.sum, kind: 'internal' },
    { key: 'cash_dividends', label: 'Dividends paid into cash', usd: cashDiv.sum, kind: 'internal' },
    // MEMO, not part of the identity. Free shares have a cost basis of zero by definition,
    // and assetsAtCost excludes DRIP lots for exactly that reason — so adding the match to
    // the explained total would claim to account for cost that was never incurred. It is
    // reported because it is real wealth the owner should see, and because it is the
    // difference between "the plan grew" and "I paid for the plan to grow".
    { key: 'employer_match', label: 'Employer match received (free shares, zero cost basis)', usd: matchAcc.sum + orphanMatch.sum, kind: 'memo' },
    // MEMO, and it exists so the exclusion above is visible. This is reserve money that came
    // out of the tracked cash ledger, where it has already been counted once. (ACCT-001)
    { key: 'reserve_from_cash', label: 'Reserve fund topped up from tracked cash (already counted in the cash ledger)', usd: rfFromCash.sum, kind: 'memo' },
  ];

  const externalUSD = doors.filter(d => d.kind === 'external').reduce((s, d) => s + d.usd, 0);
  const internalUSD = doors.filter(d => d.kind === 'internal').reduce((s, d) => s + d.usd, 0);
  const memoUSD = doors.filter(d => d.kind === 'memo').reduce((s, d) => s + d.usd, 0);
  const unknownUSD = unknown.reduce((s, u) => s + (u.usd || 0), 0);
  const unconvertible = inAcc.bad + outAcc.bad + rfAcc.bad + planAcc.bad + matchAcc.bad +
    openAcc.bad + realAcc.bad + orphanMatch.bad + cashDiv.bad;

  return { doors, externalUSD, internalUSD, memoUSD, unknown, unknownUSD, unconvertible, openingBalances };
}

/**
 * What every asset cost, on one basis, across every sleeve the app tracks.
 *
 * Holdings (retirement, crypto, the employer plan), open time deposits at principal, open
 * non-deposit trades at margin, cash balances, and the reserve fund. This is the number the
 * capital doors have to add up to; if it does not, something is unaccounted for and no
 * return percentage computed from it means anything.
 */
export function assetsAtCostUSD(S, ctx) {
  const c = ctx || {};
  const now = typeof c.toUSD === 'function' ? c.toUSD : ((v) => v);
  const costOf = typeof c.costOfHolding === 'function' ? c.costOfHolding : (() => 0);
  const parts = {};
  const acc = { sum: 0, bad: 0 };

  // A SOLD POSITION IS NOT AN ASSET. costOfHolding is math.hTotUSD, and for a closed row that
  // walks past qty*avgCost (qty is 0) and past the lots (a full exit leaves none) to the
  // monthly-plan history, answering with what the holding cost over its lifetime. Eight IBKR
  // exits left USD 5,167.93 of dead cost on the assets side of the identity while the doors —
  // correctly — explained only the cash the sale produced, pushing the residual to USD 5,054
  // against a tolerance of USD 1,539 and taking the reconciliation down.
  //
  // Selling is identity-neutral by construction: holdings fall by the cost basis, cash rises
  // by the proceeds, and realized_pnl explains the difference. That only holds if the closed
  // row contributes nothing. (audit 2026-08-06)
  const holdings = (S && S.holdings || []).reduce((s, h) => {
    if (!(h && h.qty > 0)) return s;
    const v = costOf(h);
    return s + ((typeof v === 'number' && isFinite(v)) ? v : 0);
  }, 0);
  parts.holdings = holdings; _add(acc, holdings);

  // ⚠ CONTRACT-001 — `Partial` IS A LIVE TRADE, AND THIS WAS THE ONLY LINE THAT SAID OTHERWISE.
  //
  // `Partial` is a real status: `trades.js:1444` writes it on a partial exit, `ai-actions.js:606`
  // on a chat exit, `reconcile.js:203` on a broker reconciliation, and `_originalQty` records
  // what the position used to be. **Twenty-five call sites across advisory, cash, portfolio,
  // settings, trades, search, signals, ai-chat, ai-actions and ai-copilot all filter
  // `status !== 'Closed'`, which includes it.** This one filtered `status !== 'Open'` and threw
  // it away — not a forgotten case, an inverted idiom.
  //
  // The consequence is one-sided and therefore loud: a partially exited trade still holds
  // `qty` at `entry`, so its remaining cost is genuinely an asset, while the doors correctly
  // explain only the cash the partial sale produced. The residual absorbs the whole remainder.
  //
  // Measured 2026-08-15: **zero Partial trades on his book, so this is a mine** — but the
  // trigger is "sell part of anything", and the blast radius is no longer hypothetical now that
  // ACCT-001 has brought the residual to −$153.76 against a ±$1,502 band. His three largest
  // equity positions are ASELS $1,652, ALCAR $1,623 and OYAKC $1,513: a SMALL partial exit on
  // any of them leaves a remainder big enough to push the identity outside its tolerance, and
  // `capital.reconciliation` (C1) going CRITICAL suppresses NET GROWTH and the VOO benchmark.
  //
  // Written as `=== 'Closed'` so it reads the same as the other twenty-five (rule #24).
  let tdPrincipal = 0, tradeMargin = 0;
  (S && S.trades || []).forEach(t => {
    if (!t || t.status === 'Closed') return;
    const local = (t.entry || 0) * (t.qty || 0);
    const usd = now(local, t.currency || 'USD');
    if (!isFinite(usd)) { acc.bad++; return; }
    if (t.type === 'TimeDeposit') tdPrincipal += usd; else tradeMargin += usd;
  });
  parts.tdPrincipal = tdPrincipal; _add(acc, tdPrincipal);
  parts.tradeMargin = tradeMargin; _add(acc, tradeMargin);

  let cash = 0;
  Object.entries((S && S.cash) || {}).forEach(([cur, v]) => {
    if (typeof v !== 'number' || !v) return;
    const usd = now(v, cur);
    if (isFinite(usd)) cash += usd; else acc.bad++;
  });
  parts.cash = cash; _add(acc, cash);

  let reserve = 0;
  const rf = (S && S.reserveFund) || {};
  if (typeof rf === 'object') {
    Object.entries(rf).forEach(([cur, v]) => {
      if (typeof v !== 'number' || !v) return;
      const usd = now(v, cur);
      if (isFinite(usd)) reserve += usd; else acc.bad++;
    });
  }
  parts.reserveFund = reserve; _add(acc, reserve);

  return { total: acc.sum, parts, unconvertible: acc.bad };
}

/**
 * Does the capital story add up?
 *
 * assetsAtCost should equal external capital plus internally generated additions. It will
 * never be exact — expenses paid out of the cash pool leave without a matching asset, FX is
 * marked at different instants in different places — so the answer is a residual and a
 * tolerance, not a boolean dressed up as arithmetic.
 *
 * `reconciled` is false whenever the residual is material OR any row is unclassified OR any
 * amount failed to convert. Consumers must refuse to print a return percentage when it is
 * false: an unreconciled denominator produced a +43.5% "growth" figure on this very state.
 */
/**
 * WHY THERE IS NO PER-CURRENCY BREAKDOWN HERE, AND WHY YOU SHOULD NOT ADD ONE
 *
 * It is the obvious next move: one residual says the books do not close but not WHERE, and
 * currency looks like the perfect split — structural, no inference, free. It was written,
 * measured against live state, and thrown away. The numbers:
 *
 *   USD  in $27,837   assets $18,616   → +$9,221
 *   TRY  in $56,316 − out $30,731 + realized $3,231   assets $35,450   → −$6,636
 *   EUR  in $12,226   assets $13,074   → −$848
 *   XAU / XAG                                          → exactly 0
 *
 * Those are not gaps. They are one fact: money entered as USD and bought TRY-denominated
 * assets, and THE APP HAS NO RECORD OF THE CONVERSION. A USD deposit and the TRY fund it
 * paid for are two separate rows in two separate currencies with nothing linking them. So a
 * per-currency identity books every conversion as a surplus on one side and a shortfall on
 * the other, and the two happen to be the largest numbers on the page.
 *
 * A first draft of this shipped, described the ±$900 remainder as "transaction cost", and
 * was wrong twice over: the probe behind that claim had summed holdings of THREE currencies
 * into one bucket, and the claim itself was never measured — the measured FX-of-purchase
 * effect is $369, not $1,738. The owner caught it in one sentence.
 *
 * The precondition for localising the residual is not a better split. It is recording
 * currency conversions as paired ledger rows — out of USD, into TRY, one linked pair. Until
 * that exists, the residual is a single honest number and any decomposition of it is
 * decoration. (audit 2026-08-02)
 */

export function reconcileCapital(S, ctx) {
  const doors = capitalDoors(S, ctx);                        // event-time — what he put in
  const spot = capitalDoors(S, ctx, { basis: 'spot' });       // spot — the identity
  const assets = assetsAtCostUSD(S, ctx);

  const explained = spot.externalUSD + spot.internalUSD;
  const residual = assets.total - explained;
  const fxDriftUSD = (doors.externalUSD + doors.internalUSD) - explained;
  const tolerance = Math.max(1000, Math.abs(assets.total) * 0.02);
  const reconciled = Math.abs(residual) <= tolerance &&
    doors.unknown.length === 0 &&
    doors.unconvertible === 0 &&
    assets.unconvertible === 0;

  return {
    ...doors,                       // doors[] stay event-time: that is the honest capital figure
    assetsAtCostUSD: assets.total,
    assetParts: assets.parts,
    explainedUSD: explained,        // spot basis, so it is comparable to assets
    explainedEventUSD: doors.externalUSD + doors.internalUSD,
    fxDriftUSD,                     // the cost of holding TRY while TRY fell — not a missing row
    residualUSD: residual,
    tolerance,
    reconciled,
    reasons: [
      Math.abs(residual) > tolerance ? `residual $${Math.round(residual)} exceeds tolerance $${Math.round(tolerance)}` : null,
      doors.unknown.length ? `${doors.unknown.length} unclassified cash row(s)` : null,
      (doors.unconvertible + assets.unconvertible) ? `${doors.unconvertible + assets.unconvertible} amount(s) could not be converted to USD` : null,
    ].filter(Boolean),
  };
}
